import React, { useState } from "react";
import { ArrowLeft, CheckIcon } from "lucide-react";

import { HseDetailsNotice } from "components/ui/HseDetailsNotice/HseDetailsNotice";
import { SimplePatientPageLayout } from "features/patients/components/SimplePatientPageLayout/SimplePatientPageLayout";
import { useCurrentPatientId } from "features/patients/hooks/useCurrentPatientId";
import { usePatientData } from "features/patients/hooks/usePatientData";
import { PregnancyAnteNatalConsultationForm } from "features/pregnancy/components/antenatal/PregnancyAnteNatalConsultationForm/PregnancyAnteNatalConsultationForm";
import { PregnancyAnteNatalView } from "features/pregnancy/components/antenatal/PregnancyAnteNatalView/PregnancyAnteNatalView";
import { PregnancyMessagesView } from "features/pregnancy/components/messages/PregnancyMessagesView";
import { NoPregnancySection } from "features/pregnancy/components/NoPregnancySection/NoPregnancySection";
import { PregnancyPostNatalView } from "features/pregnancy/components/postnatal/PregnancyPostNatalView/PregnancyPostNatalView";
import { AddPregnancyForm } from "features/pregnancy/components/pregnancy/AddPregnancyForm/AddPregnancyForm";
import { PatientFileReference } from "features/pregnancy/components/pregnancy/PatientFileReference";
import { PregnancyGeneralView } from "features/pregnancy/components/pregnancy/PregnancyGeneralView/PregnancyGeneralView";
import { PregnancyLoader } from "features/pregnancy/components/PregnancyLoader/PregnancyLoader";
import { PregnancySidebar } from "features/pregnancy/components/sidebar/PregnancySidebar";
import { usePregnancies } from "features/pregnancy/hooks/usePregnancies";

import { useView, views } from "./hooks/useView.js";
import s from "./PatientPregnancyPage.module.css";

const STEPS = ["Add Pregnancy", "First Ante Natal Consultation"];

const PatientPregnancyPage = () => {
  const patientId = useCurrentPatientId();
  const { data: patient } = usePatientData(patientId);
  const tenantVal = localStorage.getItem("kinesin-tenant");

  const { isLoading: isPregnanciesLoading, data: pregnanciesData } =
    usePregnancies(patientId);
  const pregnancies = pregnanciesData ?? [];

  const [isAddFormOpen, setIsAddFormOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [createdPregnancyId, setCreatedPregnancyId] = useState(null);
  // The raw query data, not the `?? []` fallback: useView has to tell "the list says there are no
  // pregnancies" apart from "the list has not arrived yet" (KIN-2047).
  const [view, setView] = useView(pregnanciesData);

  const openAddForm = () => {
    setCurrentStep(1);
    setCreatedPregnancyId(null);
    setIsAddFormOpen(true);
  };

  const closeAddForm = () => {
    setIsAddFormOpen(false);
    setCurrentStep(1);
    setCreatedPregnancyId(null);
  };

  const handlePregnancyCreated = (pregnancyId) => {
    setCreatedPregnancyId(pregnancyId);
    setCurrentStep(2);
  };

  const handleConsultationDone = () => {
    closeAddForm();
    if (createdPregnancyId) {
      setView({ name: views.anteNatal, pregnancyId: createdPregnancyId });
    }
  };

  const createdPregnancy = pregnancies.find((p) => p.id === createdPregnancyId);

  const selectedPregnancy = pregnancies.find(({ id }) => id === view.pregnancyId);
  // useView already keeps the selection inside the list; this makes it structural, so that no
  // pregnancy-scoped view can ever be rendered with an undefined pregnancy.
  const viewName = selectedPregnancy ? view.name : views.noPregnancies;

  if (isPregnanciesLoading) {
    return <PregnancyLoader>Loading pregnancies...</PregnancyLoader>;
  }

  return (
    <SimplePatientPageLayout
      sidebar={
        <PregnancySidebar
          setView={(v) => {
            closeAddForm();
            setView(v);
          }}
          pregnancies={pregnancies}
          onAddPregnancyButtonClick={openAddForm}
          view={isAddFormOpen ? { name: null, pregnancyId: null } : view}
        />
      }
    >
      {/* Shown on the pregnancy view as well as beside the Send button inside a
          consultation: MN-CMS sets PID.7 and PID.8 from the patient record, so a GP
          should see the gap before working through a visit (KIN-1733). */}
      <HseDetailsNotice
        patient={patient}
        patientId={patientId}
        submissionName="to the maternity hospital"
      />
      {isAddFormOpen ? (
        <div className={s.addPregnancyPanel}>
          <div className={s.panelHeader}>
            <button className={s.backButton} onClick={closeAddForm} type="button">
              <ArrowLeft size={20} />
            </button>
            <h2 className={s.panelTitle}>Add new pregnancy</h2>
          </div>

          <ul className={s.steps}>
            {STEPS.map((step, i) => {
              const stepNumber = i + 1;
              const isActive = currentStep === stepNumber;
              const isCompleted = currentStep > stepNumber;

              return (
                <li
                  key={step}
                  className={`${s.step} ${isActive ? s.stepActive : ""}`}
                >
                  {isCompleted ? (
                    <div className={s.stepNumberCompleted}>
                      <CheckIcon size={14} color="white" />
                    </div>
                  ) : (
                    <div className={`${s.stepNumber} ${isActive ? s.stepNumberActive : ""}`}>
                      {stepNumber}
                    </div>
                  )}
                  <span className={s.stepName}>{step}</span>
                </li>
              );
            })}
          </ul>

          <div className={s.panelContent}>
            {currentStep === 1 && <PatientFileReference patientId={patientId} />}
            {currentStep === 1 && (
              <AddPregnancyForm
                currentPatientId={patientId}
                closeModal={closeAddForm}
                tenantVal={tenantVal}
                onSuccess={handlePregnancyCreated}
              />
            )}
            {currentStep === 2 && !createdPregnancy && (
              <PregnancyLoader>Loading pregnancy data...</PregnancyLoader>
            )}
            {currentStep === 2 && !!createdPregnancy && (
              <PregnancyAnteNatalConsultationForm
                closeView={handleConsultationDone}
                tenantVal={tenantVal}
                pregnancyId={createdPregnancyId}
                patientId={patientId}
                pregnancy={createdPregnancy}
                pregnancyHistory={createdPregnancy?.history ?? ""}
              />
            )}
          </div>
        </div>
      ) : (
        <>
          {viewName === views.noPregnancies && (
            <NoPregnancySection onAddPregnancyButtonClick={openAddForm} />
          )}
          {viewName === views.general && (
            <PregnancyGeneralView
              patientId={patientId}
              tenantVal={tenantVal}
              pregnancy={selectedPregnancy}
            />
          )}
          {viewName === views.anteNatal && (
            <PregnancyAnteNatalView
              tenantVal={tenantVal}
              patientId={patientId}
              pregnancy={selectedPregnancy}
              editConsultationId={view.editConsultationId}
            />
          )}
          {viewName === views.postNatal && (
            <PregnancyPostNatalView
              patientId={patientId}
              tenantVal={tenantVal}
              pregnancy={selectedPregnancy}
            />
          )}
          {viewName === views.messages && (
            <PregnancyMessagesView
              pregnancy={selectedPregnancy}
              onSendCorrection={(consultationId) =>
                setView({
                  name: views.anteNatal,
                  pregnancyId: view.pregnancyId,
                  editConsultationId: consultationId,
                })
              }
            />
          )}
        </>
      )}
    </SimplePatientPageLayout>
  );
};

export { PatientPregnancyPage };
